import { apiClient } from './client'
import { ApiError, LlmDetectionDisabled } from './errors'
import type {
  BoxConfiguration,
  DetectionMethod,
  DetectionResult,
  FieldSuggestions,
  LlmConsent,
  SinglePage,
  Template,
  TemplateField,
} from '@/types/domain'

const http = () => apiClient.axios

/**
 * The template endpoints. Ported from `lib/services/api/templates_api.dart`
 * (425 lines), including the two places where the gateway's wire names and
 * the record vocabulary disagree — those mappings are the whole reason this
 * file is not just `axios.get`.
 */

/** Raw list item: the cheap listing uses `name`/`page_count`/`document_count`
 *  where the full template uses `title`/`numPages`/`documentCount`. */
interface TemplateListItem {
  id: string
  name?: string
  title?: string
  page_count?: number
  numPages?: number
  document_count?: number
  documentCount?: number
  fieldCount?: number
  thumbnailPath?: string
  imagePath?: string
  /** Archive listing only (`?archived=true`): RFC3339, and the **uid** of
   *  whoever archived it. */
  archivedAt?: string
  archivedBy?: string
  [key: string]: unknown
}

function normalizeTemplate(raw: Record<string, unknown>, id: string): Template {
  // The gateway omits `boxConfiguration` entirely when a page has no boxes
  // yet — which is every page of a template that is still converting or whose
  // detection has not finished, i.e. exactly the state the funnel watches.
  // `Template['pageList'][n].boxConfiguration` is not optional, so the type
  // said the key was always there and three screens read it straight
  // (`page.boxConfiguration.length`, `boxesFromConfiguration(...)`) and threw
  // "Cannot read properties of undefined (reading 'filter')" on a brand-new
  // template. Five other readers had each discovered this and written their
  // own `?? []`. Filling it once here makes the type true and all eight
  // correct.
  const withBoxes = (page: SinglePage): SinglePage => ({
    ...page,
    boxConfiguration: Array.isArray(page.boxConfiguration) ? page.boxConfiguration : [],
  })
  const pageList = Array.isArray(raw.pageList)
    ? ([...(raw.pageList as SinglePage[])].sort((a, b) => a.page - b.page).map(withBoxes) as SinglePage[])
    : // A single-page template from the older shape carries its boxes at the
      // top level rather than in a page list.
      raw.boxConfiguration
      ? [
          {
            imagePath: (raw.imagePath as string) ?? '',
            page: 0,
            uploadedAt: 0,
            boxConfiguration: (raw.boxConfiguration as BoxConfiguration[]) ?? [],
            status: 'ready' as const,
          },
        ]
      : []

  return {
    id,
    title: (raw.title as string) ?? '',
    imagePath: (raw.imagePath as string) ?? '',
    status: raw.status as string | undefined,
    folder: (raw.folder as string) ?? 'default',
    numPages: (raw.numPages as number) ?? (raw.pages as number) ?? pageList.length ?? 1,
    pageList,
    accessControl: (raw.accessControl as Template['accessControl']) ?? {
      read: [],
      write: [],
      admin: [],
    },
    fields: (raw.fields as TemplateField[]) ?? [],
    confidenceThreshold: (raw.confidenceThreshold as number) ?? 0.8,
    documentCount: (raw.documentCount as number) ?? 0,
    fieldCount: raw.fieldCount as number | undefined,
    trainingAllowed: (raw.trainingAllowed as boolean) ?? true,
    llmConsent: (raw.llmConsent as Template['llmConsent']) ?? null,
    llmModel: (raw.llmModel as string) ?? '',
    recordSchema: (raw.recordSchema as Record<string, unknown>) ?? null,
    createdAt: raw.createdAt as number | undefined,
    updatedAt: raw.updatedAt as number | undefined,
    // Both `omitempty` on the wire: absent on a live template, and
    // `archivedBy` absent on everything archived before the column existed.
    // Spread rather than assigned, so "not archived" and "archived by
    // nobody we know" both stay undefined instead of becoming ''.
    ...(typeof raw.archivedAt === 'string' && raw.archivedAt
      ? { archivedAt: raw.archivedAt }
      : {}),
    ...(typeof raw.archivedBy === 'string' && raw.archivedBy
      ? { archivedBy: raw.archivedBy }
      : {}),
  }
}

export interface UploadTemplateResult {
  id: string
  /** Present when detection ran at upload. `pending` for a PDF, whose pages
   *  do not exist yet — its detection runs in the converter. */
  detection?: DetectionResult
}

/**
 * The keys PATCH /v1/templates/:id honours. The gateway decodes the body
 * into its own template struct, so a key not listed here is accepted and
 * ignored — a typo would look like a save that worked.
 */
export interface TemplatePatch {
  title?: string
  folder?: string
  confidenceThreshold?: number
  trainingAllowed?: boolean
  /** Replaces the pages, boxes included. */
  pageList?: SinglePage[]
  /** Present: authoritative, replaces the shared field set. Absent: boxes
   *  bind to fields by label as before. */
  fields?: TemplateField[]
  /** Consent to send this template's pages to an external model, naming the
   *  approver, date, provider and region. `null` withdraws it, which stops
   *  the LLM pass and record check for this template. */
  llmConsent?: LlmConsent | null
  /** Overrides the deployment's default model for this template. The
   *  *provider* is not settable here — that is what `llmConsent` records. */
  llmModel?: string
  recordSchema?: Record<string, unknown> | null
}

/** What POST /v1/templates/:id/pages answers: the page's identity, not a
 *  full page — a PDF variant has no image until the converter has run. */
export interface AddedPage {
  page: number
  imagePath: string
  variantOf?: number | null
  label?: string | null
  status: SinglePage['status']
}

/**
 * The refusals the template editor has to explain, as the gateway codes
 * them (`templateFieldErrorResponse`, `pageErrorResponse`).
 *
 * Mapped by code rather than by message: the gateway's own texts are
 * English and written for a log, and the editor has to tell a person which
 * of these happened.
 */
export type TemplateEditErrorCode =
  | 'field_in_use'
  | 'field_name_taken'
  | 'field_type_mismatch'
  | 'duplicate_field_on_page'
  | 'field_not_found'
  | 'unknown_model'
  | 'page_not_found'
  | 'page_in_use'
  | 'page_immutable'
  | 'template_converting'
  | 'variant_invalid'

export const TEMPLATE_EDIT_ERROR_MESSAGES: Record<TemplateEditErrorCode, string> = {
  field_in_use: 'Dieses Feld ist noch auf mindestens einem Formular platziert und kann nicht gelöscht werden.',
  field_name_taken: 'Ein Feld mit diesem Namen gibt es in dieser Vorlage bereits.',
  field_type_mismatch: 'Der Feldtyp des Kästchens passt nicht zum Typ des verknüpften Feldes.',
  duplicate_field_on_page: 'Dieses Feld ist auf dieser Seite bereits platziert. Pro Seite ist es nur einmal möglich.',
  field_not_found: 'Dieses Feld gibt es in dieser Vorlage nicht (mehr).',
  unknown_model: 'Dieses KI-Modell ist unbekannt und kann nicht abgerechnet werden.',
  page_not_found: 'Diese Seite gibt es in dieser Vorlage nicht (mehr).',
  page_in_use: 'Dieser Seite sind bereits Dokumente zugeordnet.',
  page_immutable: 'Diese Seite kann nicht gelöscht werden.',
  template_converting: 'Die Seite wird noch umgewandelt. Bitte einen Moment warten.',
  variant_invalid: 'Diese Seite kann keine Variante der gewählten Seite sein.',
}

/** A refusal the editor can explain, or null when the error is something
 *  else (a network failure, a 500) and belongs in the generic handler. */
export interface TemplateEditError {
  code: TemplateEditErrorCode
  /** German, ready to render. */
  message: string
  /** On `field_in_use`: the field and how many boxes still place it — the
   *  editor offers to remove those. */
  fieldId?: string
  boxes?: number
  /** On `page_in_use`: how many documents matched the page, which is what
   *  the "trotzdem löschen" confirmation has to name. */
  documents?: number
}

export function templateEditError(error: unknown): TemplateEditError | null {
  const api = error instanceof ApiError ? error : null
  const code = api?.code
  if (!code || !(code in TEMPLATE_EDIT_ERROR_MESSAGES)) return null
  const known = code as TemplateEditErrorCode
  const details = api?.details ?? {}
  return {
    code: known,
    message: TEMPLATE_EDIT_ERROR_MESSAGES[known],
    ...(typeof details.fieldId === 'string' ? { fieldId: details.fieldId } : {}),
    ...(typeof details.boxes === 'number' ? { boxes: details.boxes } : {}),
    ...(typeof details.documents === 'number' ? { documents: details.documents } : {}),
  }
}

/** Query parameters for a template upload, or nothing when there are none —
 *  an empty object would put a `?` on every request for no reason. */
function uploadParams(input: {
  detectMethod?: DetectionMethod
  aiConsent?: boolean
}): Record<string, string> | undefined {
  const params: Record<string, string> = {}
  if (input.detectMethod) params.detectMethod = input.detectMethod
  if (input.aiConsent) params.aiConsent = 'true'
  return Object.keys(params).length > 0 ? params : undefined
}

export const templatesApi = {
  async get(id: string): Promise<Template> {
    const { data } = await http().get<Record<string, unknown>>(`/v1/templates/${id}`)
    return normalizeTemplate(data, id)
  },

  /**
   * One page of the template listing.
   *
   * `page_size` is always sent: the gateway defaults it to 20, so a listing
   * that leaves it out silently shows the first 20 templates and no screen
   * can tell that from an account with 20 templates. 100 is the gateway's
   * ceiling (a larger value is ignored, not clamped, and falls back to 20),
   * so the parameter is bounded here rather than trusted.
   */
  async list(
    options: { archived?: boolean; page?: number; pageSize?: number } = {},
  ): Promise<Template[]> {
    const pageSize = Math.min(Math.max(options.pageSize ?? 100, 1), 100)
    const { data } = await http().get<{ templates?: TemplateListItem[] } | TemplateListItem[]>(
      '/v1/templates',
      {
        params: {
          page_size: pageSize,
          ...(options.page != null ? { page: options.page } : {}),
          ...(options.archived ? { archived: 'true' } : {}),
        },
      },
    )
    const items = Array.isArray(data) ? data : (data.templates ?? [])
    return items.map((item) => {
      const raw: Record<string, unknown> = { ...item }
      raw.title ??= item.name
      raw.numPages ??= item.page_count
      raw.documentCount ??= item.document_count
      // The small grid thumbnail is the card image.
      if (item.thumbnailPath) raw.imagePath = item.thumbnailPath
      return normalizeTemplate(raw, item.id)
    })
  },

  /**
   * The caller's archived templates.
   *
   * These rows used to come back empty for everyone but an administrator —
   * the readable-ids set the listing intersected with was itself built with
   * "archived_at IS NULL", so the intersection could never match. The screen
   * that said "keine archivierten Vorlagen" was reporting that bug. Each row
   * now carries `archivedAt`, `archivedBy` (a uid), the document count and
   * the thumbnail.
   *
   * **Unpaginated**, deliberately and visibly: the gateway answers
   * `total === page_size === items.length` and `page === 1` whatever it is
   * asked, so a pager built off those numbers would always show one page of
   * everything. Hence no options here — there is nothing honest to pass.
   */
  async listArchived(): Promise<Template[]> {
    return templatesApi.list({ archived: true })
  },

  /**
   * Creates a template from an uploaded file, optionally running field
   * detection as part of the upload and *saving* what it finds.
   *
   * This is the one detection path allowed to persist: there is no unsaved
   * editor state to clobber yet. The response's `detection` is what lets the
   * caller open the editor saying "Wir haben 27 Felder erkannt — bitte
   * prüfen Sie die Zuordnung" instead of presenting the template as
   * finished. Detection is never fatal — the template is stored either way.
   */
  async upload(input: {
    file: File | Blob
    filename: string
    detectMethod?: DetectionMethod
    /** The person agreed a model may read this document. The gateway stamps
     *  the template's `llmConsent` from its own configuration; the client
     *  only reports that consent was given. */
    aiConsent?: boolean
    folderId?: string
    onProgress?: (sent: number, total: number) => void
  }): Promise<UploadTemplateResult> {
    const form = new FormData()
    form.append('file', input.file, input.filename)
    if (input.folderId) form.append('folder_id', input.folderId)

    const { data } = await http().post<UploadTemplateResult>('/v1/templates', form, {
      params: uploadParams(input),
      onUploadProgress: (event) => input.onProgress?.(event.loaded, event.total ?? 0),
      // Detection at upload runs the detector before answering; the
      // geometric one takes seconds and the model tens of seconds per page.
      timeout: input.detectMethod ? 5 * 60_000 : 60_000,
    })
    return data
  },

  /**
   * Patches a template.
   *
   * The gateway binds the body to its own `templates.Template`, so only the
   * keys in `TemplatePatch` are read — anything else is dropped silently,
   * which is why this is typed rather than a free `Record`.
   *
   * Two keys are authoritative rather than additive, and the difference is
   * the whole reason the editor has to send a whole document:
   * - `pageList` replaces the pages' box configurations.
   * - `fields` present replaces the template's shared field set: renaming a
   *   field mirrors the new name onto every bound box, including boxes on
   *   pages this PATCH did not touch, and dropping a field that is still
   *   placed is refused (409 `field_in_use`). `fields` **absent** keeps the
   *   legacy behaviour, where boxes attach to a field by label and
   *   unreferenced fields are garbage-collected.
   *
   * Errors: see `templateEditError`.
   */
  async patch(id: string, updates: TemplatePatch): Promise<void> {
    await http().patch(`/v1/templates/${id}`, updates)
  },

  /**
   * Release this form's pages to be read by a model, or take the release
   * back.
   *
   * Not a PATCH. The consent is a record — who released it, when, for which
   * vendor and region — and the worker refuses a run whose vendor or region
   * does not match the deployment's own. Only the server knows those, so the
   * server writes them; the client says yes or no and nothing else. The old
   * PATCH route could neither be trusted to name the right vendor nor clear
   * the column at all: `{llmConsent: null}` came back 200 and changed
   * nothing, which is why the screen used to tell people to ring us.
   *
   * Errors: 409 `llm_disabled` (this server runs no model at all), 403 when
   * the caller only has write access — releasing data to an outside vendor
   * is a manager's decision, not an editor's.
   */
  async grantLlmConsent(id: string): Promise<LlmConsent> {
    const { data } = await http().post<{ llmConsent: LlmConsent }>(
      `/v1/templates/${id}/llm-consent`,
    )
    return data.llmConsent
  },
  withdrawLlmConsent: async (id: string): Promise<void> => {
    await http().delete(`/v1/templates/${id}/llm-consent`)
  },
  /** The model is checked against the gateway's price table: an unpriced id
   *  is 400 `unknown_model`, because a run nobody can cost is worse than a
   *  refused setting. */
  setLlmModel: (id: string, llmModel: string) => templatesApi.patch(id, { llmModel }),
  setRecordSchema: (id: string, recordSchema: Record<string, unknown> | null) =>
    templatesApi.patch(id, { recordSchema }),

  rename: (id: string, title: string) => templatesApi.patch(id, { title }),
  setFolder: (id: string, folder: string) => templatesApi.patch(id, { folder }),
  setConfidenceThreshold: (id: string, confidenceThreshold: number) =>
    templatesApi.patch(id, { confidenceThreshold }),
  setTrainingAllowed: (id: string, trainingAllowed: boolean) =>
    templatesApi.patch(id, { trainingAllowed }),

  async archive(id: string): Promise<void> {
    await http().post(`/v1/templates/${id}/archive`)
  },

  async restore(id: string): Promise<void> {
    await http().post(`/v1/templates/${id}/restore`)
  },

  /** Hard delete. The gateway refuses with 409/400 while the template has
   *  documents unless `force` is set — there is no undo on the backend. */
  async remove(id: string, options: { force?: boolean } = {}): Promise<void> {
    await http().delete(`/v1/templates/${id}`, {
      params: options.force ? { force: 'true' } : undefined,
    })
  },

  // -- Pages and layout variants -------------------------------------------

  async addPage(
    id: string,
    input: { file: File | Blob; filename: string; variantOf?: number; label?: string },
  ): Promise<AddedPage> {
    const form = new FormData()
    form.append('file', input.file, input.filename)
    if (input.variantOf != null) form.append('variantOf', String(input.variantOf))
    if (input.label != null) form.append('label', input.label)
    const { data } = await http().post<AddedPage>(`/v1/templates/${id}/pages`, form)
    return data
  },

  /**
   * Updates a page's `label`/`variantOf`. The endpoint takes raw JSON so
   * that an explicit `null` clears a field while an absent key leaves it
   * unchanged — hence the `clearLabel`/`clearVariant` flags rather than
   * passing `null` through an optional parameter.
   */
  async updatePage(
    id: string,
    page: number,
    changes: {
      label?: string
      clearLabel?: boolean
      variantOf?: number
      clearVariant?: boolean
    },
  ): Promise<SinglePage> {
    const body: Record<string, unknown> = {}
    if (changes.clearLabel) body.label = null
    else if (changes.label != null) body.label = changes.label
    if (changes.clearVariant) body.variantOf = null
    else if (changes.variantOf != null) body.variantOf = changes.variantOf

    const { data } = await http().patch<SinglePage>(`/v1/templates/${id}/pages/${page}`, body)
    return data
  },

  /** `force` is required when documents are matched to the page (otherwise
   *  409 `page_in_use`); page 1 and the last remaining page can never be
   *  deleted (400 `page_immutable`). */
  async deletePage(id: string, page: number, options: { force?: boolean } = {}): Promise<void> {
    await http().delete(`/v1/templates/${id}/pages/${page}`, {
      params: options.force ? { force: 'true' } : undefined,
    })
  },

  // -- Shared fields --------------------------------------------------------

  async fields(id: string): Promise<Array<TemplateField & { placements: Array<{ page: number; boxId: string }> }>> {
    const { data } = await http().get<{
      fields?: Array<TemplateField & { placements: Array<{ page: number; boxId: string }> }>
    }>(`/v1/templates/${id}/fields`)
    return data.fields ?? []
  },

  /** 409 `field_in_use` while any box still references it. */
  async deleteField(id: string, fieldId: string): Promise<void> {
    await http().delete(`/v1/templates/${id}/fields/${fieldId}`)
  },

  // -- Field detection ------------------------------------------------------

  /**
   * Proposes boxes for `pages` (empty = every page with an image) and
   * returns them **without saving anything**: the editor holds unsaved
   * state, and a run that wrote to the template would be lost on the next
   * save or clobber what the person is editing.
   *
   * Send one request per page even for "alle Seiten". Three timeouts sit on
   * this path — the gateway's write deadline, its 4-minute call to
   * ml_service, and the ingress' 300s — and per-page requests stay inside
   * all of them, show progress, and leave the pages that succeeded applied
   * when a later one fails.
   */
  async suggestFields(
    id: string,
    input: { method: DetectionMethod; pages?: number[] },
  ): Promise<FieldSuggestions> {
    try {
      const { data } = await http().post<FieldSuggestions>(
        `/v1/templates/${id}/field-suggestions`,
        { method: input.method, ...(input.pages?.length ? { pages: input.pages } : {}) },
        // A model reading a page takes tens of seconds. This covers a single
        // page and sits under the gateway's own per-page budget, so a
        // timeout is reported by the side that knows why.
        { timeout: 5 * 60_000 },
      )
      return data
    } catch (error) {
      const api = ApiError.from(error)
      if (api.code === 'llm_disabled' || api.details?.error === 'llm_disabled') {
        throw new LlmDetectionDisabled()
      }
      throw api
    }
  },
}
